import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Calendar, CreditCard, XCircle, RefreshCw, Clock,
  CheckCircle, ChevronDown, ChevronUp, DollarSign,
  Users, Building, Activity, Lock, LogOut,
  Image, ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- tiny helpers ---------- */
const Badge = ({ children, variant = 'default' }) => {
  const cls = {
    paid: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-stone-200 text-stone-600',
    default: 'bg-stone-100 text-stone-700',
  }[variant] || 'bg-stone-100 text-stone-700';
  return <span className={`text-xs font-medium px-2.5 py-0.5 rounded-sm ${cls}`}>{children}</span>;
};

const StatCard = ({ icon: Icon, label, value, color = 'text-primary' }) => (
  <div className="bg-white border border-stone-200 rounded-sm p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-stone-500">{label}</span>
      <Icon className={`w-5 h-5 ${color}`} />
    </div>
    <p className={`text-2xl font-semibold ${color}`}>{value}</p>
  </div>
);

const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-stone-50 transition-colors"
        onClick={() => setOpen(!open)}
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <span className="flex items-center gap-2 font-semibold text-stone-800">
          <Icon className="w-5 h-5 text-primary" />
          {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

/* ========== LOGIN SCREEN ========== */
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
    } catch {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-100" data-testid="admin-login">
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm border border-stone-200 w-full max-w-sm">
        <div className="flex items-center justify-center mb-6">
          <Lock className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-xl font-semibold text-center mb-1">Admin Access</h1>
        <p className="text-sm text-stone-500 text-center mb-6">InFox Suites Management</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter admin password"
          className="w-full p-3 border border-stone-200 rounded-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
          autoFocus
          data-testid="admin-password-input"
        />
        {error && <p className="text-red-600 text-sm mb-3" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="w-full bg-primary text-white py-3 rounded-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          data-testid="admin-login-btn"
        >
          {loading ? 'Verifying...' : 'Sign In'}
        </button>
      </form>
    </div>
  );
};

/* ========== PHOTO MANAGER VIEW ========== */
const PhotoManager = ({ property, authHeaders, onBack }) => {
  const [images, setImages] = useState(property.images || []);
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (updated) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/properties/${property.id}/images`, { images: updated }, authHeaders());
      setImages(updated);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addUrl = () => {
    const url = newUrl.trim();
    if (!url) return;
    if (images.length >= 15) { alert('Maximum 15 images'); return; }
    save([...images, url]);
    setNewUrl('');
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this image?')) return;
    save(images.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= images.length) return;
    const arr = [...images];
    [arr[idx], arr[next]] = [arr[next], arr[idx]];
    save(arr);
  };

  return (
    <div data-testid="photo-manager">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-primary hover:underline mb-4" data-testid="photo-manager-back">
        <ArrowLeft className="w-4 h-4" /> Back to Properties
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">{property.name}</h2>
          <p className="text-sm text-stone-500">{images.length}/15 images{saving && ' — saving...'}</p>
        </div>
      </div>

      {/* Add URL */}
      <div className="flex gap-2 mb-6">
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addUrl())}
          placeholder="Paste image URL..."
          className="flex-1 p-2.5 text-sm border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={images.length >= 15}
          data-testid="photo-url-input"
        />
        <button
          onClick={addUrl}
          disabled={!newUrl.trim() || images.length >= 15}
          className="flex items-center gap-1 bg-primary text-white px-4 py-2.5 rounded-sm text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
          data-testid="photo-add-btn"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>

      {/* Image grid */}
      {images.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-12">No images yet. Add a URL above.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((url, idx) => (
            <div key={`${idx}-${url}`} className="relative group border border-stone-200 rounded-sm overflow-hidden bg-stone-100" data-testid={`photo-card-${idx}`}>
              <div className="aspect-[4/3]">
                <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.src = ''; e.target.alt = 'Failed to load'; }} />
              </div>
              {idx === 0 && (
                <span className="absolute top-2 left-2 bg-primary text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-sm flex items-center gap-1" data-testid="thumbnail-badge">
                  <Star className="w-3 h-3" /> Thumbnail
                </span>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 bg-white rounded-sm shadow hover:bg-stone-100 disabled:opacity-30" title="Move up" data-testid={`photo-move-up-${idx}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === images.length - 1} className="p-1.5 bg-white rounded-sm shadow hover:bg-stone-100 disabled:opacity-30" title="Move down" data-testid={`photo-move-down-${idx}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(idx)} className="p-1.5 bg-white rounded-sm shadow hover:bg-red-50 text-red-600" title="Delete" data-testid={`photo-delete-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[10px] text-stone-400 px-2 py-1 truncate">{url}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== MAIN ADMIN PANEL ========== */
export default function AdminPanel() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminProperties, setAdminProperties] = useState([]);
  const [editingPhotos, setEditingPhotos] = useState(null);

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const handleLogin = (t) => {
    setToken(t);
    sessionStorage.setItem('admin_token', t);
  };

  const handleLogout = () => {
    setToken('');
    sessionStorage.removeItem('admin_token');
  };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [dash, bk, pay, canc, inq, props] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, authHeaders()),
        axios.get(`${API}/admin/bookings${statusFilter ? `?status=${statusFilter}` : ''}`, authHeaders()),
        axios.get(`${API}/admin/payments`, authHeaders()),
        axios.get(`${API}/admin/cancellations`, authHeaders()),
        axios.get(`${API}/admin/deposit-inquiries`, authHeaders()),
        axios.get(`${API}/admin/properties`, authHeaders()),
      ]);
      setDashboard(dash.data);
      setBookings(bk.data.bookings);
      setPayments(pay.data.payments);
      setCancellations(canc.data.cancellations);
      setInquiries(inq.data.inquiries);
      setAdminProperties(props.data.properties);
    } catch (e) {
      if (e.response?.status === 401) handleLogout();
      console.error('Admin fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sync/ical`);
      await fetchAll();
    } catch (e) {
      console.error('Sync error', e);
    } finally {
      setSyncing(false);
    }
  };

  const updateInquiryStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/deposit-inquiries/${id}?status=${newStatus}`, {}, authHeaders());
      await fetchAll();
    } catch (e) {
      console.error('Update inquiry error', e);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking? Dates will become available again.')) return;
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/cancel`, {}, authHeaders());
      await fetchAll();
    } catch (e) {
      console.error('Cancel booking error', e);
      alert(e.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  /* ---------- NOT LOGGED IN ---------- */
  if (!token) return <AdminLogin onLogin={handleLogin} />;

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-stone-500">Loading admin panel...</div>
      </div>
    );
  }

  const d = dashboard || { bookings: {}, deposit_inquiries: {}, availability: {}, sync: null, revenue: 0 };

  return (
    <div className="bg-stone-100 min-h-screen" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-stone-900">Admin Panel</h1>
            <p className="text-sm text-stone-500 mt-1">InFox Suites Management</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={triggerSync}
              disabled={syncing}
              className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="sync-now-btn"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 border border-stone-300 text-stone-600 px-4 py-2.5 rounded-sm hover:bg-stone-50 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={DollarSign} label="Revenue" value={`€${d.revenue.toLocaleString()}`} />
          <StatCard icon={CheckCircle} label="Paid Bookings" value={d.bookings.paid || 0} color="text-emerald-600" />
          <StatCard icon={Clock} label="Pending" value={d.bookings.pending || 0} color="text-amber-600" />
          <StatCard icon={XCircle} label="Cancelled" value={d.bookings.cancelled || 0} color="text-red-500" />
        </div>

        {/* Sync Status */}
        <div className="bg-white border border-stone-200 rounded-sm p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-primary" />
            <div>
              <span className="text-sm font-medium text-stone-700">iCal Sync</span>
              <span className="text-xs text-stone-400 ml-3">
                {d.sync ? `Last: ${new Date(d.sync.timestamp).toLocaleString()} — ${d.sync.total_kept} ranges synced` : 'No sync yet'}
              </span>
            </div>
          </div>
          <span className="text-xs text-stone-400">{d.availability?.ical_blocked_ranges || 0} total blocked ranges</span>
        </div>

        <div className="space-y-6">
          {/* BOOKINGS */}
          <Section title="Bookings" icon={Calendar}>
            <div className="flex gap-2 mb-4 flex-wrap">
              {['', 'paid', 'pending', 'cancelled', 'expired'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${statusFilter === s ? 'bg-primary text-white border-primary' : 'bg-white text-stone-600 border-stone-200 hover:border-primary'}`}
                  data-testid={`filter-${s || 'all'}`}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-xs text-stone-500 uppercase tracking-wider">
                    <th className="pb-3 pr-4">Guest</th>
                    <th className="pb-3 pr-4">Property</th>
                    <th className="pb-3 pr-4">Dates</th>
                    <th className="pb-3 pr-4">Guests</th>
                    <th className="pb-3 pr-4">Total</th>
                    <th className="pb-3 pr-4">Payment</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-stone-400">No bookings found</td></tr>
                  )}
                  {bookings.map(b => (
                    <tr key={b.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-stone-800">{b.guest_name}</div>
                        <div className="text-xs text-stone-400">{b.guest_email}</div>
                      </td>
                      <td className="py-3 pr-4 text-stone-600">{b.property_id}</td>
                      <td className="py-3 pr-4 text-stone-600 whitespace-nowrap">{b.check_in_date} &rarr; {b.check_out_date}</td>
                      <td className="py-3 pr-4 text-stone-600">{b.num_guests}</td>
                      <td className="py-3 pr-4 font-medium">&euro;{b.total_price?.toFixed(2)}</td>
                      <td className="py-3 pr-4"><Badge>{b.payment_type}</Badge></td>
                      <td className="py-3 pr-4"><Badge variant={b.payment_status}>{b.payment_status}</Badge></td>
                      <td className="py-3">
                        {b.payment_status === 'paid' && (
                          <button
                            onClick={() => cancelBooking(b.id)}
                            className="text-xs text-red-600 border border-red-200 px-2.5 py-1 rounded-sm hover:bg-red-50 transition-colors"
                            data-testid={`cancel-booking-${b.id}`}
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* PAYMENTS */}
          <Section title="Payments" icon={CreditCard} defaultOpen={false}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-table">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-xs text-stone-500 uppercase tracking-wider">
                    <th className="pb-3 pr-4">Session</th>
                    <th className="pb-3 pr-4">Amount</th>
                    <th className="pb-3 pr-4">Currency</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr><td colSpan={5} className="py-8 text-center text-stone-400">No payments yet</td></tr>
                  )}
                  {payments.map(p => (
                    <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 pr-4 font-mono text-xs text-stone-500">{p.session_id?.slice(0, 24)}...</td>
                      <td className="py-3 pr-4 font-medium">&euro;{p.amount?.toFixed(2)}</td>
                      <td className="py-3 pr-4 text-stone-600">{p.currency?.toUpperCase()}</td>
                      <td className="py-3 pr-4"><Badge variant={p.payment_status}>{p.payment_status}</Badge></td>
                      <td className="py-3 text-stone-500 text-xs">{new Date(p.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* CANCELLATIONS */}
          <Section title="Cancellations" icon={XCircle} defaultOpen={false}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cancellations-table">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-xs text-stone-500 uppercase tracking-wider">
                    <th className="pb-3 pr-4">Guest</th>
                    <th className="pb-3 pr-4">Property</th>
                    <th className="pb-3 pr-4">Dates</th>
                    <th className="pb-3 pr-4">Refund</th>
                    <th className="pb-3 pr-4">Type</th>
                    <th className="pb-3">Cancelled</th>
                  </tr>
                </thead>
                <tbody>
                  {cancellations.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-stone-400">No cancellations</td></tr>
                  )}
                  {cancellations.map(c => (
                    <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 pr-4 font-medium">{c.guest_name}</td>
                      <td className="py-3 pr-4 text-stone-600">{c.property_id}</td>
                      <td className="py-3 pr-4 text-stone-600 whitespace-nowrap">{c.check_in_date} &rarr; {c.check_out_date}</td>
                      <td className="py-3 pr-4 font-medium">&euro;{c.refund_amount?.toFixed(2) || '0.00'}</td>
                      <td className="py-3 pr-4"><Badge variant={c.refund_type === 'full' ? 'paid' : 'cancelled'}>{c.refund_type || 'N/A'}</Badge></td>
                      <td className="py-3 text-xs text-stone-500">{c.cancellation_date ? new Date(c.cancellation_date).toLocaleString() : 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* DEPOSIT INQUIRIES */}
          <Section title="Deposit Inquiries" icon={Users} defaultOpen={true}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inquiries-table">
                <thead>
                  <tr className="border-b border-stone-200 text-left text-xs text-stone-500 uppercase tracking-wider">
                    <th className="pb-3 pr-4">Guest</th>
                    <th className="pb-3 pr-4">Property</th>
                    <th className="pb-3 pr-4">Dates</th>
                    <th className="pb-3 pr-4">Deposit</th>
                    <th className="pb-3 pr-4">Contact</th>
                    <th className="pb-3 pr-4">Status</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-stone-400">No deposit inquiries</td></tr>
                  )}
                  {inquiries.map(inq => (
                    <tr key={inq.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 pr-4">
                        <div className="font-medium">{inq.guest_name}</div>
                        <div className="text-xs text-stone-400">{inq.guest_email}</div>
                        <div className="text-xs text-stone-400">{inq.guest_phone}</div>
                      </td>
                      <td className="py-3 pr-4 text-stone-600">{inq.property_name}</td>
                      <td className="py-3 pr-4 text-stone-600 whitespace-nowrap">{inq.check_in_date} &rarr; {inq.check_out_date}</td>
                      <td className="py-3 pr-4 font-medium">&euro;{inq.deposit_amount?.toFixed(2)}</td>
                      <td className="py-3 pr-4"><Badge>{inq.preferred_contact}</Badge></td>
                      <td className="py-3 pr-4"><Badge variant={inq.status === 'confirmed' ? 'paid' : inq.status === 'pending' ? 'pending' : 'cancelled'}>{inq.status}</Badge></td>
                      <td className="py-3">
                        <select
                          value={inq.status}
                          onChange={e => updateInquiryStatus(inq.id, e.target.value)}
                          className="text-xs border border-stone-200 rounded-sm px-2 py-1"
                          data-testid={`inquiry-status-${inq.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* PROPERTIES & PHOTOS */}
          <Section title="Property Photos" icon={Image} defaultOpen={false}>
            {editingPhotos ? (
              <PhotoManager
                property={editingPhotos}
                authHeaders={authHeaders}
                onBack={() => { setEditingPhotos(null); fetchAll(); }}
              />
            ) : (
              <div className="space-y-2" data-testid="properties-list">
                {adminProperties.map(p => (
                  <div key={p.id} className="flex items-center justify-between bg-stone-50 px-4 py-3 rounded-sm border border-stone-200">
                    <div>
                      <span className="text-sm font-medium text-stone-800">{p.name}</span>
                      <span className="text-xs text-stone-400 ml-3">{(p.images || []).length}/15 photos</span>
                    </div>
                    <button
                      onClick={() => setEditingPhotos(p)}
                      className="text-xs text-primary border border-primary/30 px-3 py-1.5 rounded-sm hover:bg-primary/5 transition-colors"
                      data-testid={`manage-photos-${p.id}`}
                    >
                      Manage Photos
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* AVAILABILITY */}
          <Section title="Availability Overview" icon={Building} defaultOpen={false}>
            <p className="text-sm text-stone-500 mb-3">
              {d.availability?.ical_blocked_ranges || 0} blocked date ranges from Hosthub iCal sync.
              Auto-syncs every 5 minutes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                'courtiq-private-parking', 'city-nest', 'lum', 'urban-luxury', 'elysian-bliss',
                'mirroring', 'work-from-home', 'celeste-retreat', 'tranquil-getaway', 'urban-elegance'
              ].map(slug => (
                <div key={slug} className="flex items-center justify-between bg-stone-50 px-4 py-2.5 rounded-sm border border-stone-200">
                  <span className="text-sm font-medium text-stone-700">{slug}</span>
                  <a href={`/properties/${slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                    View calendar
                  </a>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
